import { useState } from 'react';
import { Button, Card, Field, Notice } from '@gatecheckpro/design-system';
import type { OrganizerEvent } from '@gatecheckpro/contracts';
import { useCommand } from '../../lib/useCommand.ts';
import { FailureNotice } from '../../lib/refusal.tsx';
import { useEventWorkspace } from './EventWorkspace.tsx';

/**
 * The public copy, and the notes that are not.
 *
 * The two are on one page and visibly separated, because the failure this
 * prevents is an organizer putting "promoter takes 40 percent" somewhere an
 * attendee can read it.
 */
export function EventDetailsPage() {
  const { event, organizationId, reload } = useEventWorkspace();
  const command = useCommand<OrganizerEvent>();

  const [title, setTitle] = useState(event.title);
  const [publicSummary, setPublicSummary] = useState(event.publicSummary ?? '');
  const [publicDescription, setPublicDescription] = useState(event.publicDescription ?? '');
  const [internalNotes, setInternalNotes] = useState(event.internalNotes ?? '');

  async function save(formEvent: React.FormEvent): Promise<void> {
    formEvent.preventDefault();
    const result = await command.run(
      `/api/v1/organizer/organizations/${organizationId}/events/${event.eventId}`,
      {
        method: 'PUT',
        body: {
          title,
          publicSummary: publicSummary === '' ? null : publicSummary,
          publicDescription: publicDescription === '' ? null : publicDescription,
          internalNotes: internalNotes === '' ? null : internalNotes,
          venueId: event.venue?.venueId ?? null,
          timezone: event.timezone,
        },
      },
    );
    if (result !== null) reload();
  }

  return (
    <div className="gc-stack gc-stack--loose" style={{ maxWidth: '42rem' }}>
      <FailureNotice error={command.error} />

      <Card title="Public details" eyebrow="Everything here is visible once published">
        <form className="gc-stack" onSubmit={(formEvent) => void save(formEvent)}>
          <Field
            label="Title"
            required
            value={title}
            onChange={(input) => setTitle(input.target.value)}
          />
          <Field
            label="Summary"
            required
            hint="One line. The first thing an attendee reads, and needed before you can publish."
            value={publicSummary}
            onChange={(input) => setPublicSummary(input.target.value)}
          />
          <div className="gc-field">
            <label className="gc-field__label" htmlFor="description">
              Description
            </label>
            <textarea
              id="description"
              className="gc-textarea"
              rows={6}
              value={publicDescription}
              onChange={(input) => setPublicDescription(input.target.value)}
            />
            <span className="gc-field__hint">
              Line-up, what to bring, how to get there. Attendees read this on the event page.
            </span>
          </div>

          <hr className="gc-divider" />

          <Notice tone="caution" title="Below this line is private">
            Internal notes never appear on the public page, in search, or in any attendee-facing
            response.
          </Notice>

          <div className="gc-field">
            <label className="gc-field__label" htmlFor="notes">
              Internal notes
            </label>
            <textarea
              id="notes"
              className="gc-textarea"
              rows={4}
              placeholder="Promoter split, production contacts, security concerns…"
              value={internalNotes}
              onChange={(input) => setInternalNotes(input.target.value)}
            />
          </div>

          <Button type="submit" variant="primary" busy={command.busy}>
            Save details
          </Button>
        </form>
      </Card>
    </div>
  );
}
